import { useState, useEffect } from "react";
import { accountService } from "../../../Api/Services/accountService";
import { SimplifiedUserInfo } from "../../../Models/User";
import {
  isValidPassword,
  passwordRequirementsMessage,
} from "../../../Utils/passwordValidator";
import { formatPhoneNumber, unformat } from "../../../Utils/DataFormatters";
import { FaCheckCircle, FaPlus, FaTimesCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import FloatingInput from "../../../Components/FloatingInput/FloatingInput";
import BackofficePage from "../../../Components/BackofficePage/BackofficePage";
import Button from "../../../Components/Button/Button";

const AdminOperatorPage = () => {

  const [operators, setOperators] = useState<SimplifiedUserInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showOperatorForm, setShowOperatorForm] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await accountService.getOperators();
      setOperators(data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem.");
      return;
    }

    if (!isValidPassword(password)) {
      toast.error(passwordRequirementsMessage);
      return;
    }

    try {
      const message = await accountService.createOperator({
        email: email,
        password: password,
        phoneNumber: unformat(phoneNumber),
      });
      toast.success(message);
    } catch (error) {
      toast.error(
        "Não foi possível criar o operador, tente novamente mais tarde!",
      );
    } finally {
      setEmail("");
      setPhoneNumber("");
      setPassword("");
      setConfirmPassword("");
      setShowOperatorForm(false);
      load();
    }
  };

  const handleToggleOperatorStatus = async (
    id: string,
    currentStatus: boolean,
  ) => {
    try {
      const updated = await accountService.setOperatorStatus(
        id,
        !currentStatus,
      );
      if (updated) {
        toast.success(
          `Operador ${!currentStatus ? "ativado" : "inativado"} com sucesso!`,
        );
        await load();
      } else {
        toast.error(
          "Não foi possível atualizar o status do operador.",
        );
      }
    } catch (err) {
      toast.error(
        "Erro ao atualizar status, tente novamente mais tarde.",
      );
    }
  };

  return (
    <div>
      <SmartNavbar />
      <BackofficePage
        title={"OPERADORES"}
        className="w-full"
      >
        {showOperatorForm ? (
          <>
            <div className="mb-4 flex flex-col sm:flex-row gap-2">
              <FloatingInput
                label={"E-mail"}
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <FloatingInput
                label={"Telefone celular"}
                type="text"
                value={formatPhoneNumber(phoneNumber)}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />

              <FloatingInput
                label={"Digite a senha"}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                tooltip={passwordRequirementsMessage.trim()}
              />

              <FloatingInput
                label={"Confirme a senha"}
                tooltip={passwordRequirementsMessage.trim()}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />

              <div className="flex gap-2">
                <Button variant="secondary"
                  onClick={() => {
                    setEmail("");
                    setPhoneNumber("");
                    setPassword("");
                    setConfirmPassword("");
                    setShowOperatorForm(false);
                  }}
                  className="w-full sm:w-32 px-4 py-2"
                >
                  {"CANCELAR"}
                </Button>

                <Button
                  onClick={handleSubmit}
                  className="w-full sm:w-32 px-4 py-2"
                >
                  {"ADICIONAR"}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <button
            className="flex items-center gap-1 my-2 text-primary-600 hover:text-primary-700"
            onClick={() => setShowOperatorForm(true)}
          >
            <FaPlus /> {"Adicionar Operador"}
          </button>
        )}

        {isLoading ? (
          <div className="mt-6 w-full space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="animate-pulse bg-white border rounded p-4 shadow-sm"
              >
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : operators.length === 0 ? (
          <div className="mt-6 border rounded p-6 bg-white shadow-sm text-center space-y-3 w-full">
            <p className="text-lg font-semibold">
              {"Nenhum operador cadastrado ainda"}
            </p>
            <p className="text-gray-700">
              {"Adicione o primeiro operador para gerenciar a equipe."}
            </p>
            <div className="flex justify-center">
              <Button
                className="flex items-center gap-2 px-4 py-2"
                onClick={() => setShowOperatorForm(true)}
              >
                <FaPlus /> {"Cadastrar operador"}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* MOBILE CARDS */}
            <div className="flex flex-col gap-4 sm:hidden">
              {operators.map((operator, index) => (
                <div
                  key={operator.id}
                  className={`border rounded p-4 shadow-sm bg-white`}
                >
                  <h2 className="font-semibold mb-2">
                    {"Operador"} {index + 1}
                  </h2>
                  <p>
                    <span className="font-medium">{"E-mail"}:</span>{" "}
                    {operator.email}
                  </p>
                  <p>
                    <span className="font-medium">
                      {"Telefone celular"}:
                    </span>{" "}
                    {operator.phoneNumber}
                  </p>
                  <p>
                    <span className="font-medium">{"Ativo"}:</span>{" "}
                    {operator.isActive ? "Sim" : "Não"}
                  </p>
                  <button
                    onClick={() =>
                      handleToggleOperatorStatus(operator.id, operator.isActive)
                    }
                    className={`flex items-center gap-1 mt-1 hover:underline ${
                      operator.isActive ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {operator.isActive
                      ? "Inativar"
                      : "Ativar"}
                  </button>
                </div>
              ))}
            </div>

            {/* DESKTOP TABLE */}
            <table
              className={`hidden sm:table w-full border text-left mb-20`}
            >
              <thead>
                <tr className="bg-primary-120">
                  <th className="border p-3 text-center">
                    {"E-mail"}
                  </th>
                  <th className="border p-3 text-center">
                    {"Telefone celular"}
                  </th>
                  <th className="border p-3 text-center">
                    {"Ativo"}
                  </th>
                  <th className="border p-3 text-center">
                    {"Ações"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {operators.map((operator: SimplifiedUserInfo) => (
                  <tr key={operator.id} className="border-t">
                    <td className="border p-3 text-center">{operator.email}</td>
                    <td className="border p-3 text-center">
                      {formatPhoneNumber(operator.phoneNumber)}
                    </td>
                    <td className="border p-3 text-center">
                      <div className="flex justify-center items-center">
                        {operator.isActive ? (
                          <FaCheckCircle className="text-green-600" />
                        ) : (
                          <FaTimesCircle className="text-red-600" />
                        )}
                      </div>
                    </td>
                    <td className="border p-3 text-center space-x-2">
                      <button
                        onClick={() =>
                          handleToggleOperatorStatus(
                            operator.id,
                            operator.isActive,
                          )
                        }
                        className={`hover:underline ${
                          operator.isActive ? "text-red-600" : "text-green-600"
                        }`}
                      >
                        {operator.isActive
                          ? "Inativar"
                          : "Ativar"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </BackofficePage>
    </div>
  );
};

export default AdminOperatorPage;
